// Knife blade finishes (no three.js here, so settings can import it cheaply). Patterns are drawn
// procedurally (finishTexture.ts); the names are our own.

export type Pattern =
  /** Gradient along the blade with marbled strokes. */
  | 'marble'
  /** Dark polished base with bright swirling blotches. */
  | 'doppler'
  /** Smooth multi-colour fade from base to tip. */
  | 'fade'
  /** Orange with black brush stripes. */
  | 'tiger'
  /** Black with a red web. */
  | 'web'
  /** Steel with blue, gold and purple heat patches. */
  | 'patches'
  /** Folded steel layers. */
  | 'damascus'
  /** One colour, brushed. */
  | 'solid'
  /** Rusted metal. */
  | 'rust'
  /** Red with pale blotches. */
  | 'butcher';

export interface FinishDef {
  label: string;
  pattern: Pattern;
  /** Pattern colours: marble/fade use them as gradient stops, the others as base then highlights. */
  colors: readonly string[];
  /** Marble strokes on the first and second half ("r,g,b"). */
  warm?: string;
  cool?: string;
  /** Handle inserts and ring. */
  accent: string;
}

const marble = (label: string, colors: string[], warm: string, cool: string, accent: string): FinishDef => ({
  label,
  pattern: 'marble',
  colors,
  warm,
  cool,
  accent,
});

export const FINISHES = {
  fireice: marble(
    'Fire & Ice',
    ['#b0100a', '#ff3d12', '#ff9a34', '#f6eeff', '#78c8ff', '#2a6cff', '#1532c4'],
    '90,0,0',
    '0,10,80',
    '#1b2f7a',
  ),
  ruby: {
    label: 'Ruby',
    pattern: 'doppler',
    colors: ['#2a0006', '#d4102c', '#ff3c5a', '#7a0016'],
    accent: '#5a0812',
  },
  sapphire: {
    label: 'Sapphire',
    pattern: 'doppler',
    colors: ['#03113f', '#1e5bff', '#5fb0ff', '#0b2a9e'],
    accent: '#0d2266',
  },
  blackpearl: {
    label: 'Black Pearl',
    pattern: 'doppler',
    colors: ['#07060f', '#3b2a72', '#8a3d9c', '#1c3d78'],
    accent: '#241a3d',
  },
  toxic: {
    label: 'Toxic',
    pattern: 'doppler',
    colors: ['#021a0b', '#14c45a', '#7dff9c', '#0a6a5a'],
    accent: '#0b3d22',
  },
  midnight: {
    label: 'Midnight Pink',
    pattern: 'doppler',
    colors: ['#14031c', '#c0288a', '#4a1aa0', '#ff70c0'],
    accent: '#3d0f3a',
  },
  sunset: {
    label: 'Sunset Fade',
    pattern: 'fade',
    colors: ['#f7d43a', '#ff8a2a', '#ff3d8a', '#a23cff', '#3a4cff'],
    accent: '#3a1452',
  },
  tiger: {
    label: 'Tiger',
    pattern: 'tiger',
    colors: ['#f0a018', '#ffcf4a', '#1a0d02'],
    accent: '#5a3505',
  },
  lace: {
    label: 'Crimson Lace',
    pattern: 'web',
    colors: ['#120607', '#c4141c'],
    accent: '#4a080b',
  },
  oilslick: {
    label: 'Oil Slick',
    pattern: 'patches',
    colors: ['#8a9098', '#2a52c8', '#d8a838', '#7a3aa8'],
    accent: '#1f2f5a',
  },
  damascus: {
    label: 'Damascus',
    pattern: 'damascus',
    colors: ['#5c626b', '#c9ced4', '#2c3036'],
    accent: '#22252a',
  },
  butcher: {
    label: 'Butcher',
    pattern: 'butcher',
    colors: ['#9a0d10', '#e2474a', '#f7c0b0'],
    accent: '#4a0607',
  },
  ultraviolet: {
    label: 'Ultraviolet',
    pattern: 'solid',
    colors: ['#2a1a4a', '#3d2a66'],
    accent: '#1e1433',
  },
  rust: {
    label: 'Rust',
    pattern: 'rust',
    colors: ['#5a2a12', '#a8531e', '#2a140a'],
    accent: '#3a1c0c',
  },
  ocean: marble(
    'Ocean',
    ['#062038', '#0e4f7a', '#1d93b3', '#c9f4ff', '#3fc6d6', '#127199', '#062a4a'],
    '0,30,60',
    '0,40,70',
    '#0b3550',
  ),
  emerald: marble(
    'Emerald',
    ['#02130b', '#09522d', '#1fae5c', '#c6ffde', '#2fd07a', '#0a6a3b', '#03190f'],
    '0,40,10',
    '0,40,20',
    '#073d22',
  ),
  gold: marble(
    'Gold',
    ['#4a3005', '#a27508', '#f2c94c', '#fff6d0', '#f2c94c', '#a27508', '#4a3005'],
    '70,40,0',
    '70,45,0',
    '#4a3305',
  ),
  night: marble(
    'Night',
    ['#08090c', '#15181e', '#262a33', '#3b404b', '#262a33', '#15181e', '#08090c'],
    '0,0,0',
    '0,0,0',
    '#121419',
  ),
  steel: marble(
    'Steel',
    ['#4e555f', '#7e8792', '#b9c0c8', '#f0f3f6', '#b9c0c8', '#7e8792', '#4e555f'],
    '30,34,40',
    '30,34,40',
    '#1c1f24',
  ),
} satisfies Record<string, FinishDef>;
export type Finish = keyof typeof FINISHES;

export const FINISH_IDS = Object.keys(FINISHES) as Finish[];

/** A random finish other than `current` (`rnd` in 0..1). */
export function randomFinish(current: Finish, rnd: () => number = Math.random): Finish {
  const others = FINISH_IDS.filter((f) => f !== current);
  return others[Math.min(others.length - 1, Math.floor(rnd() * others.length))];
}
